import { apiService } from "../../../services";
import {
	fileTypes,
	IAddStudyData,
	ICompletionData,
	IDownloadUrlData,
	IDeleteFilePayload,
	IFileDownloadPayload,
	IPresignedUrlsResponse,
	IStandardApiResponse,
	storyType,
} from "../models/interfaces";
import { generateFileName } from "../transformer";
import { IfileDetails } from "src/pages/ManageStudies/models/interfaces";
import { assertSafeHttpUrl } from "src/utils/security";

export const getAllStudies = async () => {
	return await apiService.get("entities/basic");
};

// In-flight promise cache — collapses concurrent dropdown callers (mount + post-auth effect) into one network request.
let inFlightDropdown: Promise<any> | null = null;
export const getDropdownValues = async () => {
	if (inFlightDropdown) return inFlightDropdown;
	inFlightDropdown = apiService.get("masters/dropdown").finally(() => {
		inFlightDropdown = null;
	});
	return inFlightDropdown;
};

export const invalidateDropdownInFlight = () => {
	inFlightDropdown = null;
};

export const getAdminDropdownValues = async () => {
	return await apiService.get("admin/profiles-roles");
};

export const getAllUser = async (study_id: string) => {
	return await apiService.get("masters/allusers", { study_id });
};

export const getAllUserAdmin = async (study_id: string) => {
	return await apiService.get("admin/allusers", { study_id });
};

// In-flight promise cache — collapses concurrent callers into one network request since React state guards race.
let inFlightMeta: Promise<any> | null = null;
export const getMetaDetails = async () => {
	if (inFlightMeta) return inFlightMeta;
	inFlightMeta = apiService.get("masters/metadata").finally(() => {
		inFlightMeta = null;
	});
	return inFlightMeta;
};

export const invalidateMetaInFlight = () => {
	inFlightMeta = null;
};

export const getNotesPreviewData = async (values) => {
	return await apiService.post("notes/request_preview", { ...values });
};

export const getNotesColumnMapping = async (values) => {
	return await apiService.get("notes/mapping/", { ...values });
};

export const saveNotesColumnMapping = async (values) => {
	return await apiService.put("notes/mapping", { ...values });
};

export const editStudyStar = async (values) => {
	return await apiService.put("entities/star", values);
};

export const deleteFiles = async (values: IDeleteFilePayload) => {
	return await apiService.post("files/", values);
};

export const getCollaborators = async (search_string: string) => {
	return await apiService.get("entities/collaborator", { search_string });
};

export const addCollaborators = async (values) => {
	return await apiService.post("entities/addCollaborator", values);
};

export const addUsers = async (values) => {
	return await apiService.post("entities/collaborator", values);
};

export const addUsersAdmin = async (values) => {
	return await apiService.post("admin/collaborator", values);
};

export const sendTrackingData = async (values) => {
	return await apiService.post("activity/time-tracking", values);
};

export const registerInterest = async (values) => {
	return await apiService.post("masters/register-feature-interest", values);
};
export const registerSuccess = async (values) => {
	return await apiService.post("masters/register-feature-success", values);
};

export const getPresignedUrls = async (
	study_type_name: string,
	study_id: string,
	file_type: string,
	file_details: IfileDetails[],
	startDate?: string,
	endDate?: string,
	id_type?: string,
): Promise<IPresignedUrlsResponse[]> => {
	const payload: any = { study_type_name, study_id, file_type, file_details };
	if (startDate) payload.start_date = startDate;
	if (endDate) payload.end_date = endDate;
	if (id_type) payload.id_type = id_type;
	const response = await apiService.post<IStandardApiResponse<IPresignedUrlsResponse[]>>("files/initiate_upload", payload);
	return response.data ?? [];
};

export const getPresignedUrl = async (file_name: string, study_id: string, file_type): Promise<IPresignedUrlsResponse> => {
	return await apiService.post("files/get-presigned-url", { study_id, file_name, file_type });
};

export const completeMultipartUpload = async (completionData: ICompletionData) => {
	return await apiService.post("files/complete_upload", completionData);
};

export const downloadFile = async (values: IFileDownloadPayload) => {
	const { file_name, file_type } = values;
	const fileExtension = file_name?.split(".").pop();

	const downloadFormatForReports = file_type === fileTypes.report || file_type === fileTypes.notes ? fileExtension : "";
	const ques_id = "";

	const fileName = generateFileName(file_type, file_name, ques_id, downloadFormatForReports);
	try {
		const response = await apiService.get<IStandardApiResponse<IDownloadUrlData>>("files/download", values);
		const downloadUrl = response.data?.url;
		if (!downloadUrl) {
			throw new Error("No download URL received");
		}
		const fileResponse = await fetch(assertSafeHttpUrl(downloadUrl));
		if (fileResponse.ok) {
			const blob = await fileResponse.blob(); // Convert the response to a blob
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName; // Use the original file name for download
			document.body.appendChild(a); // Append the anchor to the body
			a.click(); // Trigger the download
			document.body.removeChild(a); // Remove the anchor from the body
			window.URL.revokeObjectURL(url); // Clean up the object URL
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const downloadMappingTemplate = async (values: { study_id: string; id_type: string; study_type: storyType }) => {
	const { id_type, study_type } = values;
	const fileName = `${id_type}_mapping_template.xlsx`;

	const apiUrl = study_type === storyType.type4 ? "quant/download/mapping_template" : "files/download/mapping_template";

	try {
		const response = await apiService.downloadTemplateFile(apiUrl, values);
		const url = window.URL.createObjectURL(response);
		const a = document.createElement("a");
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	} catch (err) {
		console.error("Error downloading mapping template: ", err);
		throw err;
	}
};

// Function to upload a single chunk to S3
const uploadChunk = async (presignedUrl, file, index, CHUNK_SIZE, totalChunks, onProgress) => {
	const start = index * CHUNK_SIZE;
	const end = Math.min(start + CHUNK_SIZE, file.size);
	const chunk = file.slice(start, end); // Get the chunk from the file

	const { url } = presignedUrl; // Presigned S3 URL

	// Step 1: Upload the chunk to S3 using the presigned URL with `fetch`
	const response = await fetch(assertSafeHttpUrl(url), {
		method: "PUT",
		body: chunk,
		headers: {
			// You can add content type or other necessary headers here
			// 'Content-Type': 'application/octet-stream', // Optional: Set to binary upload
		},
	});

	// Check if the upload was successful
	if (!response.ok) {
		throw new Error(`Error uploading chunk ${index + 1}: ${response.statusText}`);
	}

	// Step 2: Calculate progress manually since fetch doesn't support `onUploadProgress`
	const percentCompleted = ((index + 1) / totalChunks) * 100;
	onProgress?.({ percent: percentCompleted });

	// Step 3: Extract ETag from the response headers
	const etag = response.headers.get("etag");

	// Return the ETag and PartNumber for this chunk
	return {
		PartNumber: index + 1,
		ETag: etag,
	};
};

// Function to handle the chunked upload process
export const uploadFileInChunksToS3 = async (presigned_urls_info, file, CHUNK_SIZE) => {
	const totalChunks = Math.ceil(file.size / CHUNK_SIZE); // If the file is small enough for a single upload, handle it as a single chunk

	const etags = await Promise.all(
		Array.from({ length: totalChunks }).map((_, index) => uploadChunk(presigned_urls_info[index], file, index, CHUNK_SIZE, totalChunks, file.onProgress)),
	);

	return etags;
};

export const uploadFileToS3 = async (url, file) => {
	// Step 1: Create a new AbortController to handle upload cancellation if needed
	const controller = new AbortController();
	const { signal } = controller;

	// Step 2: Upload the file to S3 using fetch
	const uploadResponse = await fetch(assertSafeHttpUrl(url), {
		method: "PUT",
		body: file,
		headers: {
			"Content-Type": file.type,
		},
		signal, // Attach the AbortController's signal to handle cancellation
	});

	// Step 3: Check if the upload was successful
	if (!uploadResponse.ok) {
		throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
	}

	// Step 4: Return the response from the fetch call
	return uploadResponse;
};

// In-flight promise cache keyed by args; collapses concurrent fetchAllStudies callers into one network request.
const inFlightStudies = new Map<string, Promise<any>>();
export const getAllStudiesDetails = async (tab?: string, sort_by?: string, sort_order?: string) => {
	const key = `${tab ?? ""}|${sort_by ?? ""}|${sort_order ?? ""}`;
	const pending = inFlightStudies.get(key);
	if (pending) return pending;
	const promise = apiService.get("folder/studies", { tab, sort_by, sort_order }).finally(() => {
		inFlightStudies.delete(key);
	});
	inFlightStudies.set(key, promise);
	return promise;
};

// Drop pending studies promises so the next call fires with fresh headers.
export const invalidateStudiesInFlight = () => {
	inFlightStudies.clear();
};

export const createFolder = async (values) => {
	return await apiService.post("folder/", { ...values });
};

export const updateFolder = async (values) => {
	return await apiService.put("folder/", { ...values });
};

export const deleteFolder = async (folder_id: string, delete_option: string) => {
	return await apiService.delete("folder/", { folder_id, delete_option });
};

export const moveToFolder = async (folder_id: string, study_ids: string[]) => {
	return await apiService.put("folder/assign-study", { folder_id, study_ids });
};

export const getFolderDeleteOptions = async (folder_id: string) => {
	return await apiService.get("folder/folder-actions", { folder_id });
};

export const fetchTranscripts = async (groupKey: string): Promise<{ transcripts: { path: string; name: string }[] }> => {
	return await apiService.get<{ transcripts: { path: string; name: string }[] }>("/studies/transcripts", { group_key: groupKey });
};
